import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '@rehome/shared';
import { reviewAdoption, toast } from '@rehome/shared';
import type { AdoptionLead } from '@rehome/shared';
import { Loader2, CheckCircle2, XCircle, Phone, Mail, Clock, PawPrint, ChevronRight, AlertTriangle, MessageSquare } from 'lucide-react';

/**
 * Az indoklás alsó és felső hossza.
 *
 * Az alsó korlát nem formalitás: egy "ok" vagy "nem" annyit ér, mintha semmi
 * nem érkezett volna, az örökbefogadó viszont pont ebből tudja meg, mi
 * történt. A felső korlát a notifications üzenetébe is beépülő szöveget
 * tartja kordában.
 */
const MIN_REVIEW_NOTE = 20;
const MAX_REVIEW_NOTE = 1000;

interface LeadWithAnimal extends AdoptionLead {
  animalName?: string;
  animalSpecies?: string;
  animalBreed?: string;
  animalImageUrl?: string;
}

const AdoptionReviewPage: React.FC = () => {
  const { shelterId } = useParams<{ shelterId: string }>();
  const [leads, setLeads] = useState<LeadWithAnimal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processingId, setProcessingId] = useState<string | null>(null);
  /**
   * Melyik igénylést bírálja épp, és milyen döntéssel.
   *
   * Egyetlen állapot mindkét kimenetre: az indoklás az ELFOGADÁSHOZ is
   * kötelező, tehát ugyanaz a megerősítő panel jön fel, csak más színnel és
   * más szöveggel. Két külön állapottal (elfogad / elutasít) elő tudna
   * fordulni, hogy mindkettő nyitva van egyszerre.
   */
  const [deciding, setDeciding] = useState<{ leadId: string; action: 'accept' | 'reject' } | null>(null);
  const [reviewNote, setReviewNote] = useState('');
  const [expandedLeadId, setExpandedLeadId] = useState<string | null>(null);
  const [contactRevealedIds, setContactRevealedIds] = useState<Set<string>>(new Set());

  const fetchLeads = useCallback(async () => {
    if (!shelterId) return;
    setLoading(true);
    setError(null);
    try {
      // Fetch pending leads via animals in this shelter
      const { data: animalsData, error: animalsErr } = await supabase
        .from('animals')
        .select('id, name, species, breed, image_url')
        .eq('shelter_id', shelterId)
        .eq('status', 'AdoptionPending');

      if (animalsErr) throw animalsErr;
      if (!animalsData || animalsData.length === 0) { setLeads([]); setLoading(false); return; }

      const animalIds = animalsData.map(a => a.id);

      const { data: leadsData, error: leadsErr } = await supabase
        .from('adoption_leads')
        .select('*')
        .in('animal_id', animalIds)
        .in('status', ['Pending', 'Contacted'])
        .order('created_at', { ascending: false });

      if (leadsErr) throw leadsErr;

      const merged: LeadWithAnimal[] = (leadsData || []).map(lead => {
        const animal = animalsData.find(a => a.id === lead.animal_id);
        return {
          id: lead.id,
          animalId: lead.animal_id,
          adopterId: lead.adopter_id,
          name: lead.name,
          email: lead.email,
          phone: lead.phone,
          message: lead.message,
          status: lead.status,
          notes: lead.notes,
          rejectionReason: lead.rejection_reason,
          reviewNote: lead.review_note,
          reviewedAt: lead.reviewed_at,
          contactRevealed: lead.contact_revealed,
          createdAt: lead.created_at,
          updatedAt: lead.updated_at,
          animalName: animal?.name,
          animalSpecies: animal?.species,
          animalBreed: animal?.breed,
          animalImageUrl: animal?.image_url,
        };
      });

      // Restore contact revealed state
      setContactRevealedIds(new Set(merged.filter(l => l.contactRevealed).map(l => l.id)));
      setLeads(merged);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [shelterId]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const openDecision = (leadId: string, action: 'accept' | 'reject') => {
    // Ugyanarra a gombra másodszor kattintva bezárul; másik döntésre váltva
    // a szöveg törlődik, nehogy az elfogadásnak szánt indoklás menjen el
    // elutasításként.
    setDeciding(prev => (prev?.leadId === leadId && prev.action === action ? null : { leadId, action }));
    setReviewNote('');
  };

  const submitDecision = async () => {
    if (!deciding) return;
    const note = reviewNote.trim();
    if (note.length < MIN_REVIEW_NOTE) {
      toast.error(`Az indoklás legyen legalább ${MIN_REVIEW_NOTE} karakter — az örökbefogadó ezt fogja elolvasni.`);
      return;
    }

    setProcessingId(deciding.leadId);
    const result = await reviewAdoption(deciding.leadId, deciding.action, note);
    setProcessingId(null);
    if (result.success) {
      setDeciding(null);
      setReviewNote('');
      fetchLeads();
    }
  };

  const handleContact = async (leadId: string) => {
    setProcessingId(leadId);
    const result = await reviewAdoption(leadId, 'contact');
    setProcessingId(null);
    if (result.success) {
      setContactRevealedIds(prev => new Set(prev).add(leadId));
      fetchLeads();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto py-8 px-4">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Örökbefogadási igények</h1>
          <p className="text-gray-500 mt-1">Beérkezett igények elbírálása</p>
        </div>
        <Link to={`/shelter/${shelterId}/animals`} className="text-teal-600 hover:underline text-sm">← Vissza az állatokhoz</Link>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 text-red-700 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" /> {error}
        </div>
      )}

      {leads.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
          <Clock className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h3 className="text-xl font-bold text-gray-800 mb-2">Nincs függőben lévő igény</h3>
          <p className="text-gray-500">Jelenleg nincs elbírálásra vagy kapcsolatfelvételre váró örökbefogadási igény.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {leads.map(lead => {
            const isExpanded = expandedLeadId === lead.id;
            const isRevealed = contactRevealedIds.has(lead.id) || lead.contactRevealed;
            const isProcessing = processingId === lead.id;
            // A bírálati panel csak ANNÁL az igénylésnél nyílik ki, amelyiket
            // épp elbírálják — a lista összes eleme ugyanezt az állapotot nézi.
            const decision = deciding?.leadId === lead.id ? deciding : null;

            return (
              <div key={lead.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                {/* Header */}
                <div className="p-5 flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl bg-gray-200 overflow-hidden flex-shrink-0">
                    {lead.animalImageUrl ? (
                      <img src={lead.animalImageUrl} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><PawPrint className="w-6 h-6 text-gray-400" /></div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-bold text-gray-800 truncate">{lead.animalName || 'Ismeretlen állat'}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${lead.status === 'Contacted' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
                        {lead.status === 'Contacted' ? 'Kapcsolatfelvétel' : 'Elbírálás alatt'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500">
                      {lead.animalSpecies} · {lead.animalBreed || '—'} · Igénylő: {lead.name}
                    </p>
                    <p className="text-xs text-gray-400">{new Date(lead.createdAt).toLocaleDateString('hu-HU', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                  </div>
                  <button onClick={() => setExpandedLeadId(isExpanded ? null : lead.id)}
                    className={`p-2 rounded-lg transition-colors ${isExpanded ? 'bg-teal-100 text-teal-700' : 'text-gray-400 hover:bg-gray-100'}`}>
                    <ChevronRight className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                  </button>
                </div>

                {/* Expanded details */}
                {isExpanded && (
                  <div className="px-5 pb-5 border-t border-gray-100 pt-4 space-y-4">
                    {/* Adopter info */}
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">Igénylő neve</p>
                        <p className="font-medium">{lead.name}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Email</p>
                        {isRevealed ? (
                          <p className="font-medium text-teal-700">{lead.email}</p>
                        ) : (
                          <p className="text-gray-400 italic">Adatvédelmi okokból rejtett</p>
                        )}
                      </div>
                      <div>
                        <p className="text-gray-500">Telefonszám</p>
                        {isRevealed ? (
                          <p className="font-medium text-teal-700">{lead.phone || 'Nincs megadva'}</p>
                        ) : (
                          <p className="text-gray-400 italic">Adatvédelmi okokból rejtett</p>
                        )}
                      </div>
                      <div>
                        <p className="text-gray-500">Igénylés dátuma</p>
                        <p className="font-medium">{new Date(lead.createdAt).toLocaleDateString('hu-HU')}</p>
                      </div>
                    </div>

                    {lead.message && (
                      <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
                        <strong>Üzenet:</strong> {lead.message}
                      </div>
                    )}

                    {/* Action buttons */}
                    <div className="flex gap-3 pt-2">
                      <button onClick={() => openDecision(lead.id, 'accept')} disabled={isProcessing}
                        className={`flex-1 py-2.5 rounded-xl font-bold disabled:opacity-50 flex items-center justify-center gap-2 transition-colors ${
                          decision?.action === 'accept' ? 'bg-green-700 text-white ring-2 ring-green-300' : 'bg-green-600 text-white hover:bg-green-700'
                        }`}>
                        <CheckCircle2 className="w-4 h-4" />
                        Elfogadás
                      </button>

                      <button onClick={() => openDecision(lead.id, 'reject')} disabled={isProcessing}
                        className={`flex-1 py-2.5 rounded-xl font-bold disabled:opacity-50 flex items-center justify-center gap-2 transition-colors ${
                          decision?.action === 'reject' ? 'bg-red-700 text-white ring-2 ring-red-300' : 'bg-red-600 text-white hover:bg-red-700'
                        }`}>
                        <XCircle className="w-4 h-4" />
                        Elutasítás
                      </button>

                      {!isRevealed && (
                        <button onClick={() => handleContact(lead.id)} disabled={isProcessing}
                          className="flex-1 bg-amber-600 text-white py-2.5 rounded-xl font-bold hover:bg-amber-700 disabled:opacity-50 flex items-center justify-center gap-2">
                          {isProcessing && processingId === lead.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Phone className="w-4 h-4" />}
                          Kapcsolatfelvétel
                        </button>
                      )}

                      {isRevealed && (
                        <div className="flex-1 flex items-center gap-2 text-sm text-green-700 bg-green-50 rounded-xl px-4 py-2.5 justify-center">
                          <Mail className="w-4 h-4" />
                          Elérhetőség látható
                        </div>
                      )}
                    </div>

                    {/* ── Indoklás ─────────────────────────────────────
                        Mindkét döntéshez kötelező. Ez a szöveg megy el az
                        örökbefogadónak, ezért a mező ki is mondja: nem
                        belső jegyzet. */}
                    {decision && (
                      <div className={`rounded-xl p-4 space-y-3 border ${
                        decision.action === 'accept' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                      }`}>
                        <div className="flex items-start gap-2">
                          <MessageSquare className={`w-4 h-4 mt-0.5 ${decision.action === 'accept' ? 'text-green-700' : 'text-red-700'}`} />
                          <div>
                            <label htmlFor={`note-${lead.id}`} className={`block text-sm font-semibold ${decision.action === 'accept' ? 'text-green-800' : 'text-red-800'}`}>
                              {decision.action === 'accept' ? 'Elfogadás indoklása (kötelező)' : 'Elutasítás indoklása (kötelező)'}
                            </label>
                            <p className="text-xs text-gray-500 mt-0.5">
                              Ezt a szöveget <strong>{lead.name}</strong> el fogja olvasni a saját felületén.
                            </p>
                          </div>
                        </div>

                        <textarea
                          id={`note-${lead.id}`}
                          value={reviewNote}
                          onChange={e => setReviewNote(e.target.value.slice(0, MAX_REVIEW_NOTE))}
                          placeholder={decision.action === 'accept'
                            ? 'Pl.: A bemutatkozásod és a kertes ház alapján Bodri jó helyen lesz nálatok. A következő lépés a szerződés véglegesítése.'
                            : 'Pl.: Bodri kertes házat igényel, a mostani lakhatás sajnos nem felel meg neki. Kérlek, nézd meg a kisebb kutyáinkat!'}
                          className={`w-full border rounded-lg p-3 text-sm min-h-[96px] outline-none focus:ring-2 ${
                            decision.action === 'accept' ? 'border-green-300 focus:ring-green-500' : 'border-red-300 focus:ring-red-500'
                          }`}
                        />

                        <div className="flex items-center justify-between gap-3">
                          <span className={`text-xs ${reviewNote.trim().length < MIN_REVIEW_NOTE ? 'text-gray-400' : 'text-gray-500'}`}>
                            {reviewNote.trim().length} / {MIN_REVIEW_NOTE} karakter
                            {reviewNote.trim().length < MIN_REVIEW_NOTE && ' — még kevés'}
                          </span>
                          <div className="flex gap-2">
                            <button onClick={() => { setDeciding(null); setReviewNote(''); }}
                              className="text-gray-500 px-4 py-2 text-sm hover:text-gray-700">
                              Mégsem
                            </button>
                            <button
                              onClick={submitDecision}
                              disabled={isProcessing || reviewNote.trim().length < MIN_REVIEW_NOTE}
                              className={`px-4 py-2 rounded-lg font-bold text-sm text-white disabled:opacity-50 flex items-center gap-2 ${
                                decision.action === 'accept' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'
                              }`}
                            >
                              {isProcessing && processingId === lead.id && <Loader2 className="w-4 h-4 animate-spin" />}
                              {decision.action === 'accept' ? 'Elfogadás véglegesítése' : 'Elutasítás véglegesítése'}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdoptionReviewPage;
